import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Gamepad2,
  Search,
  ArrowLeft,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  Calendar,
  Key,
  CheckCircle,
  XCircle,
  ExternalLink
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GenreSelector, parseGenres, formatGenres } from "@/components/ui/genre-selector";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { X, Image, Upload, Trash2 as Trash2Icon } from "lucide-react";

interface Game {
  id: string;
  title: string;
  description: string;
  cover_image_url: string;
  is_published: boolean;
  created_at: string;
  available_keys: number;
  total_keys: number;
  developer_id: string;
  developer?: {
    display_name: string;
    studio_name?: string;
  };
  allow_requests: boolean;
  price: number;
}

export default function AdminGames() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [publishFilter, setPublishFilter] = useState<string>("all");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<Game | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [gameToEdit, setGameToEdit] = useState<Game | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [existingCoverImage, setExistingCoverImage] = useState<string>("");
  const [existingScreenshots, setExistingScreenshots] = useState<string[]>([]);
  const [deletedScreenshots, setDeletedScreenshots] = useState<string[]>([]);
  const [coverImageDeleted, setCoverImageDeleted] = useState(false);

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    genres: [] as string[],
    steam_url: "",
    steam_game_id: "",
    price: 0,
    available_keys: 0,
    total_keys: 0,
    is_published: false,
    allow_requests: false,
    release_date: "",
    highlight_id: 1,
    highlight_end_date: "",
    youtube_trailer_url: ""
  });

  useEffect(() => {
    if (user?.id) {
      checkAdminAccess();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAdmin) {
      fetchGames();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterGames();
  }, [games, searchQuery, publishFilter]);

  const checkAdminAccess = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Error checking admin access");
        navigate("/dashboard");
      }
    }
  };

  const fetchGames = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("games")
        .select(`
          *,
          developer:profiles!games_developer_id_fkey(
            display_name,
            studio_name
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setGames((data as Game[]) || []);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const filterGames = () => {
    let filtered = [...games];

    if (searchQuery) {
      filtered = filtered.filter(game =>
        game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (game.developer?.display_name && game.developer.display_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (game.developer?.studio_name && game.developer.studio_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (publishFilter === "published") {
      filtered = filtered.filter(game => game.is_published);
    } else if (publishFilter === "unpublished") {
      filtered = filtered.filter(game => !game.is_published);
    }

    setFilteredGames(filtered);
  };

  const handleTogglePublish = async (game: Game) => {
    if (isToggling) return;

    setIsToggling(true);
    try {
      const newStatus = !game.is_published;
      const { data, error } = await supabase
        .from("games")
        .update({ is_published: newStatus })
        .eq("id", game.id)
        .select();

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('Update failed: No rows affected. Check RLS policies.');
      }
      toast.success(`Game ${newStatus ? 'published' : 'unpublished'} successfully!`);

      // Update local state immediately
      setGames(games.map(g => g.id === game.id ? { ...g, is_published: newStatus } : g));

      // Refresh from server
      await fetchGames();
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      toast.error(`Failed to update game: ${error.message || 'Unknown error'}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!gameToDelete || deleteConfirmation !== gameToDelete.title) {
      toast.error("Game title doesn't match. Please type the exact game title.");
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("games")
        .delete()
        .eq("id", gameToDelete.id);

      if (error) {
        throw error;
      }
      toast.success("Game deleted successfully!");
      setIsDeleteDialogOpen(false);
      setGameToDelete(null);
      setDeleteConfirmation("");
      await fetchGames();
    } catch (error) {
      console.error("Error deleting game:", error);
      toast.error(`Failed to delete game: ${error.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditGameOpen = (game: Game) => {
    setGameToEdit(game);
    setEditForm({
      title: game.title,
      description: game.description,
      genres: parseGenres((game as any).genre || ""),
      steam_url: (game as any).steam_url || "",
      steam_game_id: (game as any).steam_game_id ? String((game as any).steam_game_id) : "",
      price: game.price,
      available_keys: game.available_keys,
      total_keys: game.total_keys,
      is_published: game.is_published,
      allow_requests: game.allow_requests,
      release_date: (game as any).release_date || "",
      highlight_id: (game as any).highlight_id || 1,
      highlight_end_date: (game as any).highlight_end_date || "",
      youtube_trailer_url: (game as any).youtube_trailer_url || ""
    });
    setExistingCoverImage(game.cover_image_url || "");
    setExistingScreenshots((game as any).screenshots || []);
    setDeletedScreenshots([]);
    setCoverImageDeleted(false);
    setCoverImageFile(null);
    setScreenshotFiles([]);
    setIsEditing(true);
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setCoverImageFile(file);
    }
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    setScreenshotFiles(prev => [...prev, ...validFiles]);
  };

  const removeScreenshotFile = (index: number) => {
    setScreenshotFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingScreenshot = (index: number) => {
    const screenshotToDelete = existingScreenshots[index];
    setExistingScreenshots(prev => prev.filter((_, i) => i !== index));
    setDeletedScreenshots(prev => [...prev, screenshotToDelete]);
  };

  const removeExistingCoverImage = () => {
    setExistingCoverImage("");
    setCoverImageDeleted(true);
  };

  const restoreExistingScreenshot = (screenshotUrl: string) => {
    setExistingScreenshots(prev => [...prev, screenshotUrl]);
    setDeletedScreenshots(prev => prev.filter(url => url !== screenshotUrl));
  };

  const restoreExistingCoverImage = () => {
    setExistingCoverImage(gameToEdit?.cover_image_url || "");
    setCoverImageDeleted(false);
  };

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const { error: uploadError } = await supabase.storage
      .from('game-images')
      .upload(path, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('game-images')
      .getPublicUrl(path);

    return data.publicUrl;
  };

  const handleSaveEdit = async () => {
    if (!gameToEdit) return;

    setIsUploading(true);
    try {
      let coverImageUrl = existingCoverImage;
      let screenshotUrls = existingScreenshots;

      // Upload cover image if changed
      if (coverImageFile) {
        const coverPath = `covers/${gameToEdit.id}/${Date.now()}-${coverImageFile.name}`;
        coverImageUrl = await uploadImage(coverImageFile, coverPath);
      } else if (coverImageDeleted) {
        coverImageUrl = null;
      }

      // Upload new screenshots
      if (screenshotFiles.length > 0) {
        const newScreenshotUrls = await Promise.all(
          screenshotFiles.map(async (file, index) => {
            const screenshotPath = `screenshots/${gameToEdit.id}/${Date.now()}-${index}-${file.name}`;
            return await uploadImage(file, screenshotPath);
          })
        );
        screenshotUrls = [...screenshotUrls, ...newScreenshotUrls];
      }

      // Update game in database
      const { data, error } = await supabase
        .from("games")
        .update({
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          genre: formatGenres(editForm.genres),
          cover_image_url: coverImageUrl,
          screenshots: screenshotUrls,
          steam_url: editForm.steam_url.trim() || null,
          steam_game_id: editForm.steam_game_id ? parseInt(editForm.steam_game_id) : null,
          price: editForm.price,
          release_date: editForm.release_date,
          highlight_id: editForm.highlight_id,
          highlight_end_date: editForm.highlight_end_date || null,
          youtube_trailer_url: editForm.youtube_trailer_url || null,
        })
        .eq("id", gameToEdit.id)
        .select();

      if (error) {
        toast.error(error.message);
      } else {
        if (!data || data.length === 0) {
          throw new Error('Update failed: No rows affected. Check RLS policies.');
        }

        toast.success("Game updated successfully!");
        setIsEditing(false);
        setGameToEdit(null);
        // Reset form states
        setCoverImageFile(null);
        setScreenshotFiles([]);
        setDeletedScreenshots([]);
        setCoverImageDeleted(false);
        await fetchGames();
      }
    } catch (error: any) {
      console.error('Error updating game:', error);
      toast.error(`Error updating game: ${error.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading games...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold">Manage Games</h1>
              <p className="text-muted-foreground">{filteredGames.length} games found</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search by title or developer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Select value={publishFilter} onValueChange={setPublishFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Games</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="unpublished">Unpublished</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Games List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGames.map((game) => (
            <Card key={game.id} className="overflow-hidden">
              <div className="relative">
                {game.cover_image_url ? (
                  <img
                    src={game.cover_image_url}
                    alt={game.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-muted flex items-center justify-center">
                    <Gamepad2 className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <Badge variant={game.is_published ? "default" : "secondary"}>
                    {game.is_published ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Published
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Unpublished
                      </>
                    )}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">{game.title}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {new Date(game.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Key className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {game.available_keys} / {game.total_keys} keys
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-muted-foreground">Developer:</span>
                  <span className="text-sm font-medium">
                    {game.developer?.studio_name || game.developer?.display_name || "Unknown"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleTogglePublish(game);
                    }}
                    className="flex-1"
                    disabled={isToggling || isDeleting}
                  >
                    {game.is_published ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" />
                        Unpublish
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Publish
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleEditGameOpen(game);
                    }}
                    className="flex-1"
                    disabled={isToggling || isDeleting || isEditing}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setGameToDelete(game);
                      setDeleteConfirmation("");
                      setIsDeleteDialogOpen(true);
                    }}
                    disabled={isToggling || isDeleting}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredGames.length === 0 && (
          <Card className="p-8 text-center">
            <Gamepad2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No games found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete Game</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-custom-paddings">
                <p className="text-sm text-destructive font-medium">
                  ⚠️ This action cannot be undone. This will permanently delete the game and all associated data.
                </p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Type the game title to confirm deletion:
                </p>
                <p className="text-sm font-medium mb-2">
                  Game title: {gameToDelete?.title}
                </p>
                <Input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Enter game title here..."
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={handleDeleteGame}
                  disabled={deleteConfirmation !== gameToDelete?.title || isDeleting}
                  className="flex-1"
                >
                  {isDeleting ? "Deleting..." : "Delete Game"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setGameToDelete(null);
                    setDeleteConfirmation("");
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Game Dialog */}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent className="bg-card max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Game</DialogTitle>
            </DialogHeader>
            {gameToEdit && (
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Game Title</Label>
                    <Input
                      id="edit-title"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="edit-genres">Genres</Label>
                      {editForm.genres.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setEditForm({ ...editForm, genres: [] })}
                          className="text-sm text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="mt-2">
                      <GenreSelector
                        value={editForm.genres}
                        onChange={(genres) => setEditForm({ ...editForm, genres })}
                        placeholder="Select genres..."
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <RichTextEditor
                    content={editForm.description}
                    onChange={(content) => setEditForm({ ...editForm, description: content })}
                    placeholder="Describe your game with rich text formatting..."
                  />
                </div>
                <div>
                  <Label htmlFor="edit-cover">
                    Cover Image <span className="text-muted-foreground text-sm font-normal">(Recommendations: 1920x550 & Centered Logo)</span>
                  </Label>

                  {/* Existing cover image */}
                  {existingCoverImage && !coverImageDeleted && (
                    <div className="mt-2 space-y-2">
                      <div className="relative group">
                        <img
                          src={existingCoverImage}
                          alt="Current cover"
                          className="w-full h-32 object-cover rounded-custom-paddings"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={removeExistingCoverImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Current cover image</p>
                    </div>
                  )}

                  {/* Deleted cover image (can be restored) */}
                  {coverImageDeleted && (
                    <div className="mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-custom-paddings">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <X className="w-4 h-4 text-destructive" />
                          <span className="text-sm text-destructive">Cover image removed</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={restoreExistingCoverImage}
                        >
                          Restore
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Upload new cover image */}
                  <Input
                    id="edit-cover"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="cursor-pointer mt-2"
                  />
                  {coverImageFile && (
                    <div className="mt-2 flex items-center gap-2">
                      <Image className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600">{coverImageFile.name}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCoverImageFile(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="edit-screenshots">Screenshots</Label>

                  {/* Existing screenshots */}
                  {existingScreenshots.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm font-medium">Current screenshots:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {existingScreenshots.map((screenshot, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={screenshot}
                              alt={`Screenshot ${index + 1}`}
                              className="w-full h-20 object-cover rounded-custom-paddings cursor-pointer"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeExistingScreenshot(index)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Deleted screenshots (can be restored) */}
                  {deletedScreenshots.length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-sm font-medium text-destructive">Removed screenshots:</p>
                      <div className="space-y-1">
                        {deletedScreenshots.map((screenshot, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-destructive/10 border border-destructive/20 rounded">
                            <div className="flex items-center gap-2">
                              <X className="w-4 h-4 text-destructive" />
                              <span className="text-sm text-destructive">Screenshot {index + 1}</span>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => restoreExistingScreenshot(screenshot)}
                            >
                              Restore
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload new screenshots */}
                  <Input
                    id="edit-screenshots"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleScreenshotChange}
                    className="cursor-pointer mt-2"
                  />
                  {screenshotFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-green-600">New screenshots to add:</p>
                      {screenshotFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Image className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-green-600">{file.name}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeScreenshotFile(index)}
                          >
                            <Trash2Icon className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-price">Price (€)</Label>
                    <Input
                      id="edit-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Game price in euros (used to calculate coin cost)
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="edit-youtube_trailer_url">YouTube Trailer URL</Label>
                    <Input
                      id="edit-youtube_trailer_url"
                      type="url"
                      value={editForm.youtube_trailer_url || ''}
                      onChange={(e) => setEditForm({ ...editForm, youtube_trailer_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      YouTube video URL for the game trailer
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="edit-steam">Steam URL *</Label>
                    <Input
                      id="edit-steam"
                      type="url"
                      value={editForm.steam_url}
                      onChange={(e) => setEditForm({ ...editForm, steam_url: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-steam_game_id">Steam Game ID *</Label>
                    <Input
                      id="edit-steam_game_id"
                      type="number"
                      value={editForm.steam_game_id}
                      onChange={(e) => setEditForm({ ...editForm, steam_game_id: e.target.value })}
                      placeholder="e.g. 2448830"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Steam App ID (for playtime tracking)
                    </p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-release-date">Release Date</Label>
                  <Input
                    id="edit-release-date"
                    type="date"
                    value={editForm.release_date}
                    onChange={(e) => setEditForm({ ...editForm, release_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit-highlight-id">Highlight Level</Label>
                  <select
                    id="edit-highlight-id"
                    value={editForm.highlight_id}
                    onChange={(e) => setEditForm({ ...editForm, highlight_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                  >
                    <option value={1}>Default (1)</option>
                    <option value={2}>Featured (2)</option>
                    <option value={3}>Premium (3)</option>
                    <option value={4}>VIP (4)</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Defines how prominently your game is displayed to creators
                  </p>
                </div>
                <div>
                  <Label htmlFor="edit-highlight-end-date">Highlight End Date (Optional)</Label>
                  <Input
                    id="edit-highlight-end-date"
                    type="date"
                    value={editForm.highlight_end_date}
                    onChange={(e) => setEditForm({ ...editForm, highlight_end_date: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty for permanent highlight. Set a date to automatically end the highlight period.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <Upload className="w-4 h-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Game"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setGameToEdit(null);
                      setCoverImageFile(null);
                      setScreenshotFiles([]);
                      setDeletedScreenshots([]);
                      setCoverImageDeleted(false);
                    }}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

